// FilterSemanticValidator.ts - Zod-based semantic validation for filter expressions

import { z } from 'zod';
import {
  FilterAST,
  Expression,
  FieldNode,
  OperatorNode,
  ValueNode,
} from './FilterAST';
import {
  FilterCriteria,
  HumanOperator,
  Operator,
  MappedShipDbToHumanOperators,
} from '../../constants';

export interface SemanticError {
  type: 'semantic';
  subtype:
    | 'invalid_field'
    | 'invalid_operator'
    | 'invalid_value'
    | 'invalid_combination';
  message: string;
  field?: string;
  operator?: string;
  value?: string;
  suggestions?: string[];
  position: { start: number; end: number };
}

export interface SemanticWarning {
  type: 'warning';
  message: string;
  position: { start: number; end: number };
}

export interface SemanticValidationResult {
  isValid: boolean;
  errors: SemanticError[];
  warnings: SemanticWarning[];
}

export class FilterSemanticValidator {
  private filterOptions: Record<string, FilterCriteria>;

  constructor(filterOptions: Record<string, FilterCriteria>) {
    this.filterOptions = filterOptions;
  }

  validate(ast: FilterAST): SemanticValidationResult {
    const errors: SemanticError[] = [];
    const warnings: SemanticWarning[] = [];

    // Validate each expression
    for (const expression of ast.expressions) {
      const result = this.validateExpression(expression);
      errors.push(...result.errors);
      warnings.push(...result.warnings);
    }

    const isValid = errors.length === 0;


    return { isValid, errors, warnings };
  }

  private validateExpression(expression: Expression): SemanticValidationResult {
    const errors: SemanticError[] = [];
    const warnings: SemanticWarning[] = [];

    // Skip validation for GroupExpressions - validate their content recursively
    if (expression.type === 'GroupExpression' && expression.content) {
      return this.validate(expression.content);
    }

    // Must have a field
    if (!expression.field) {
      return { isValid: false, errors: [], warnings: [] }; // Parser handles this
    }

    // Validate field exists
    const fieldValidation = this.validateField(expression.field);
    if (!fieldValidation.isValid) {
      errors.push(...fieldValidation.errors);
      return { isValid: false, errors, warnings };
    }

    const fieldConfig = this.getFieldConfig(expression.field.name);
    if (!fieldConfig) {
      return { isValid: false, errors, warnings }; // Should not happen after field validation
    }

    // For boolean fields, only field is required
    if (this.isBooleanField(expression.field.name)) {
      if (expression.operator || expression.value) {
        warnings.push({
          type: 'warning',
          message: `Boolean field "${expression.field.name}" does not need an operator or value`,
          position: expression.position,
        });
      }
      return { isValid: true, errors, warnings };
    }

    // For non-boolean fields, validate operator and value
    if (!expression.operator) {
      // Parser handles incomplete expressions
      return { isValid: false, errors: [], warnings: [] };
    }

    const operatorValidation = this.validateOperator(
      expression.field,
      expression.operator,
      fieldConfig
    );
    if (!operatorValidation.isValid) {
      errors.push(...operatorValidation.errors);
    }

    if (!expression.value) {
      // Parser handles incomplete expressions
      return { isValid: false, errors: [], warnings: [] };
    }

    const valueValidation = this.validateValue(
      expression.field,
      expression.operator,
      expression.value,
      fieldConfig
    );
    if (!valueValidation.isValid) {
      errors.push(...valueValidation.errors);
      warnings.push(...valueValidation.warnings);
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  private validateField(field: FieldNode): SemanticValidationResult {
    const errors: SemanticError[] = [];
    const upperFieldName = field.name.toUpperCase();

    // Check if field exists (case-insensitive)
    if (!this.filterOptions[upperFieldName]) {
      const suggestions = this.getSimilarFields(field.name);
      errors.push({
        type: 'semantic',
        subtype: 'invalid_field',
        message: `Unknown field "${field.name}"`,
        field: field.name,
        suggestions,
        position: field.position,
      });
      return { isValid: false, errors, warnings: [] };
    }

    return { isValid: true, errors: [], warnings: [] };
  }

  private validateOperator(
    field: FieldNode,
    operator: OperatorNode,
    fieldConfig: FilterCriteria
  ): SemanticValidationResult {
    const errors: SemanticError[] = [];
    const operatorValue = operator.operator;

    // Convert raw operator to HumanOperator for validation
    const humanOperator = this.operatorToHumanOperator(operatorValue);
    if (!humanOperator) {
      errors.push({
        type: 'semantic',
        subtype: 'invalid_operator',
        message: `Invalid operator "${operatorValue}"`,
        field: field.name,
        operator: operatorValue,
        position: operator.position,
      });
      return { isValid: false, errors, warnings: [] };
    }

    // Convert API operator strings to HumanOperator enums for comparison
    const allowedHumanOperators = this.convertApiOperatorsToHumanOperators(
      fieldConfig.operators
    );

    // Check if operator is allowed for this field type
    if (!allowedHumanOperators.includes(humanOperator)) {
      const allowedOps = allowedHumanOperators
        .map((op) => this.humanOperatorToSymbol(op))
        .join(', ');
      errors.push({
        type: 'semantic',
        subtype: 'invalid_operator',
        message: `Operator "${operatorValue}" is not allowed for field "${field.name}". Allowed operators: ${allowedOps}`,
        field: field.name,
        operator: operatorValue,
        suggestions: allowedHumanOperators.map((op) =>
          this.humanOperatorToSymbol(op)
        ),
        position: operator.position,
      });
      return { isValid: false, errors, warnings: [] };
    }

    return { isValid: true, errors: [], warnings: [] };
  }

  private convertApiOperatorsToHumanOperators(
    apiOperators: any[]
  ): HumanOperator[] {
    const humanOperators: HumanOperator[] = [];

    for (const apiOp of apiOperators) {
      // Handle both string operators from API and HumanOperator enums from static config
      if (typeof apiOp === 'string') {
        const mappedOp = MappedShipDbToHumanOperators[apiOp];
        if (mappedOp) {
          humanOperators.push(mappedOp);
        }
      } else if (Object.values(HumanOperator).includes(apiOp)) {
        // It's already a HumanOperator enum
        humanOperators.push(apiOp);
      }
    }

    return humanOperators;
  }

  private validateValue(
    field: FieldNode,
    operator: OperatorNode,
    value: ValueNode,
    fieldConfig: FilterCriteria
  ): SemanticValidationResult {
    const errors: SemanticError[] = [];
    const warnings: SemanticWarning[] = [];

    // Extract value without quotes
    const rawValue = value.value;
    const cleanValue = value.isQuoted ? rawValue : rawValue;

    // Validate based on field data type
    const dataType = fieldConfig.data_types[0]; // Use primary data type

    // Special handling for circle position fields with INSIDE/OUTSIDE operators
    if (field.name.toUpperCase() === 'CIRCLE_POSITION') {
      const humanOperator = this.operatorToHumanOperator(operator.operator);
      if (
        humanOperator === HumanOperator.InsideCircle ||
        humanOperator === HumanOperator.OutsideCircle
      ) {
        return this.validateCirclePositionValue(
          cleanValue,
          field,
          operator,
          value,
          errors,
          warnings
        );
      }
    }

    // Special validation for number fields with quoted values
    if (dataType === 'number' && value.isQuoted) {
      // For number fields, quoted values should generally not be allowed unless they're wildcards
      // AND the field supports wildcard operators, OR it's an MMSI field with leading zeros
      const isMmsiField = field.name.toUpperCase().includes('MMSI');
      const hasLeadingZero =
        cleanValue.startsWith('0') && cleanValue.length > 1;

      if (this.isWildcardValue(cleanValue)) {
        // Check if this number field supports wildcard operators
        const allowedHumanOperators = this.convertApiOperatorsToHumanOperators(
          fieldConfig.operators
        );
        const supportsWildcards = allowedHumanOperators.some((op) =>
          [
            HumanOperator.Contains,
            HumanOperator.NotContains,
            HumanOperator.StartsWith,
            HumanOperator.DoesNotStartWith,
            HumanOperator.EndsWith,
            HumanOperator.DoesNotEndWith,
          ].includes(op)
        );

        if (!supportsWildcards) {
          const allowedOps = allowedHumanOperators
            .map((op) => this.humanOperatorToSymbol(op))
            .join(', ');
          errors.push({
            type: 'semantic',
            subtype: 'invalid_value',
            message: `Number field "${field.name}" does not support wildcard values. Allowed operators: ${allowedOps}`,
            field: field.name,
            operator: operator.operator,
            value: rawValue,
            suggestions: [cleanValue.replace(/\*/g, '')], // Suggest without wildcards
            position: value.position,
          });
          return { isValid: false, errors, warnings };
        }
      } else if (!(isMmsiField && hasLeadingZero)) {
        // Non-wildcard quoted value on number field (except MMSI with leading zeros)
        errors.push({
          type: 'semantic',
          subtype: 'invalid_value',
          message: `Number field "${field.name}" should not use quoted values. Use ${cleanValue} instead of "${cleanValue}"`,
          field: field.name,
          operator: operator.operator,
          value: rawValue,
          suggestions: [cleanValue], // Suggest the unquoted version
          position: value.position,
        });
        return { isValid: false, errors, warnings };
      }
      // If it's an MMSI field with leading zeros, allow the quoted value to preserve them
    }

    // Special validation for string fields with unquoted values
    if (dataType === 'string' && !value.isQuoted) {
      // For string fields, unquoted values should generally not be allowed
      // Check if the value looks like a number that should be quoted
      const isNumericValue =
        !isNaN(Number(cleanValue)) && cleanValue.trim() !== '';
      if (isNumericValue) {
        errors.push({
          type: 'semantic',
          subtype: 'invalid_value',
          message: `String field "${field.name}" should use quoted values. Use "${cleanValue}" instead of ${cleanValue}`,
          field: field.name,
          operator: operator.operator,
          value: rawValue,
          suggestions: [`"${cleanValue}"`], // Suggest the quoted version
          position: value.position,
        });
        return { isValid: false, errors, warnings };
      }
    }

    try {
      switch (dataType) {
        case 'number':
          this.validateNumberValue(
            cleanValue,
            field,
            operator,
            value,
            fieldConfig,
            errors,
            warnings
          );
          break;
        case 'string':
          this.validateStringValue(
            cleanValue,
            field,
            operator,
            value,
            fieldConfig,
            errors,
            warnings
          );
          break;
        case 'boolean':
          // Boolean fields shouldn't have values, but this is handled in validateExpression
          break;
      }
    } catch (error) {
      errors.push({
        type: 'semantic',
        subtype: 'invalid_value',
        message: `Invalid value "${rawValue}" for field "${field.name}"`,
        field: field.name,
        operator: operator.operator,
        value: rawValue,
        position: value.position,
      });
    }

    // Validate against predefined values if available
    if (fieldConfig.values && fieldConfig.values.length > 0) {
      // Skip predefined value validation for geometric operators that use dynamic UUIDs/IDs
      const humanOperator = this.operatorToHumanOperator(operator.operator);
      const isGeometricOperator =
        humanOperator &&
        [
          HumanOperator.InsideArea,
          HumanOperator.OutsideArea,
          HumanOperator.InsideCircle,
          HumanOperator.OutsideCircle,
        ].includes(humanOperator);

      if (!isGeometricOperator) {
        this.validatePredefinedValues(
          cleanValue,
          field,
          value,
          fieldConfig,
          errors,
          warnings
        );
      }
    }

    // Validate against limits
    if (fieldConfig.limit) {
      this.validateLimits(
        cleanValue,
        field,
        value,
        fieldConfig,
        errors,
        warnings
      );
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  private validateCirclePositionValue(
    cleanValue: string,
    field: FieldNode,
    operator: OperatorNode,
    value: ValueNode,
    errors: SemanticError[],
    warnings: SemanticWarning[]
  ): SemanticValidationResult {
    // Circle position should be in format: (latitude, longitude, radius)
    // Example: (57.03, 9.77, 100000)

    // Check if the value matches the expected format
    const circleFormatRegex =
      /^\s*\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)\s*$/;
    const match = cleanValue.match(circleFormatRegex);

    if (!match) {
      errors.push({
        type: 'semantic',
        subtype: 'invalid_value',
        message: `Circle position must be in format (latitude, longitude, radius). Example: (57.03, 9.77, 100000)`,
        field: field.name,
        operator: operator.operator,
        value: cleanValue,
        suggestions: ['(57.03, 9.77, 100000)', '(55.676, 12.568, 50000)'], // Example suggestions
        position: value.position,
      });
      return { isValid: false, errors, warnings };
    }

    const [, latStr, lonStr, radiusStr] = match;
    const latitude = parseFloat(latStr);
    const longitude = parseFloat(lonStr);
    const radius = parseFloat(radiusStr);

    // Validate latitude range (-90 to 90)
    if (latitude < -90 || latitude > 90) {
      errors.push({
        type: 'semantic',
        subtype: 'invalid_value',
        message: `Latitude must be between -90 and 90 degrees. Got: ${latitude}`,
        field: field.name,
        operator: operator.operator,
        value: cleanValue,
        position: value.position,
      });
    }

    // Validate longitude range (-180 to 180)
    if (longitude < -180 || longitude > 180) {
      errors.push({
        type: 'semantic',
        subtype: 'invalid_value',
        message: `Longitude must be between -180 and 180 degrees. Got: ${longitude}`,
        field: field.name,
        operator: operator.operator,
        value: cleanValue,
        position: value.position,
      });
    }

    // Validate radius (should be positive and reasonable)
    if (radius <= 0) {
      errors.push({
        type: 'semantic',
        subtype: 'invalid_value',
        message: `Radius must be a positive number in meters. Got: ${radius}`,
        field: field.name,
        operator: operator.operator,
        value: cleanValue,
        position: value.position,
      });
    } else if (radius > 20000000) {
      // 20,000 km max radius (half Earth circumference)
      warnings.push({
        type: 'warning',
        message: `Radius ${radius} meters seems very large. Consider if this is correct.`,
        position: value.position,
      });
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  private validateNumberValue(
    cleanValue: string,
    field: FieldNode,
    operator: OperatorNode,
    value: ValueNode,
    fieldConfig: FilterCriteria,
    errors: SemanticError[],
    warnings: SemanticWarning[]
  ): void {
    // For wildcard operators on numbers, allow string patterns
    const isWildcardOperator = this.isWildcardValue(cleanValue);

    if (!isWildcardOperator) {
      // Must be a valid number
      const numValue = Number(cleanValue);
      if (isNaN(numValue)) {
        errors.push({
          type: 'semantic',
          subtype: 'invalid_value',
          message: `Value "${cleanValue}" must be a number for field "${field.name}"`,
          field: field.name,
          operator: operator.operator,
          value: cleanValue,
          position: value.position,
        });
        return;
      }

      // For comparison operators, ensure it makes sense
      if (['>', '<', '>=', '<='].includes(operator.operator)) {
        if (
          numValue < 0 &&
          ['COG', 'HEADING'].includes(field.name.toUpperCase())
        ) {
          warnings.push({
            type: 'warning',
            message: `Negative value for ${field.name} might not be meaningful`,
            position: value.position,
          });
        }
      }
    } else {
      // Wildcard on number field - ensure operator supports it
      const humanOp = this.operatorToHumanOperator(operator.operator);
      if (
        humanOp &&
        ![
          HumanOperator.Equal,
          HumanOperator.NotEqual,
          HumanOperator.Contains,
          HumanOperator.NotContains,
          HumanOperator.StartsWith,
          HumanOperator.EndsWith,
        ].includes(humanOp)
      ) {
        errors.push({
          type: 'semantic',
          subtype: 'invalid_combination',
          message: `Wildcard values cannot be used with "${operator.operator}" operator`,
          field: field.name,
          operator: operator.operator,
          value: cleanValue,
          position: value.position,
        });
      }
    }
  }

  private validateStringValue(
    cleanValue: string,
    field: FieldNode,
    operator: OperatorNode,
    value: ValueNode,
    fieldConfig: FilterCriteria,
    errors: SemanticError[],
    warnings: SemanticWarning[]
  ): void {
    // String validation is more permissive, but we can check for patterns

    // Validate wildcard usage with operators
    if (this.isWildcardValue(cleanValue)) {
      const humanOp = this.operatorToHumanOperator(operator.operator);
      if (
        humanOp &&
        ![HumanOperator.Equal, HumanOperator.NotEqual].includes(humanOp)
      ) {
        // Note: For string contains, it should be = "*value*", not a separate contains operator
        warnings.push({
          type: 'warning',
          message: `For string contains, use = "*${cleanValue.replace(
            /\*/g,
            ''
          )}*" instead of ${operator.operator}`,
          position: value.position,
        });
      }
    }

    // Validate specific string patterns
    if (
      field.name.toUpperCase() === 'BS_MMSI' ||
      field.name.toUpperCase() === 'MMSI'
    ) {
      if (!this.isWildcardValue(cleanValue) && cleanValue.length !== 9) {
        warnings.push({
          type: 'warning',
          message: `MMSI should be 9 digits`,
          position: value.position,
        });
      }
    }

    if (field.name.toUpperCase() === 'IMO') {
      if (!this.isWildcardValue(cleanValue) && cleanValue.length !== 7) {
        warnings.push({
          type: 'warning',
          message: `IMO should be 7 digits`,
          position: value.position,
        });
      }
    }
  }

  private validatePredefinedValues(
    cleanValue: string,
    field: FieldNode,
    value: ValueNode,
    fieldConfig: FilterCriteria,
    errors: SemanticError[],
    warnings: SemanticWarning[]
  ): void {
    // Skip validation for wildcard values
    if (this.isWildcardValue(cleanValue)) {
      return;
    }

    const upperValue = cleanValue.toUpperCase();
    const validValues = fieldConfig.values.map((v) =>
      v.toString().toUpperCase()
    );

    if (!validValues.includes(upperValue)) {
      const suggestions = this.getSimilarValues(cleanValue, fieldConfig.values);
      errors.push({
        type: 'semantic',
        subtype: 'invalid_value',
        message: `Invalid value "${cleanValue}" for field "${field.name}"`,
        field: field.name,
        value: cleanValue,
        suggestions: suggestions.slice(0, 3), // Limit to top 3 suggestions
        position: value.position,
      });
    }
  }

  private validateLimits(
    cleanValue: string,
    field: FieldNode,
    value: ValueNode,
    fieldConfig: FilterCriteria,
    errors: SemanticError[],
    warnings: SemanticWarning[]
  ): void {
    const limits = fieldConfig.limit;

    // Skip wildcard values for most limit checks
    if (this.isWildcardValue(cleanValue)) {
      return;
    }

    // String length limits
    if (limits.maxChars && cleanValue.length > limits.maxChars) {
      errors.push({
        type: 'semantic',
        subtype: 'invalid_value',
        message: `Value "${cleanValue}" exceeds maximum length of ${limits.maxChars} characters`,
        field: field.name,
        value: cleanValue,
        position: value.position,
      });
    }

    if (limits.minChars && cleanValue.length < limits.minChars) {
      errors.push({
        type: 'semantic',
        subtype: 'invalid_value',
        message: `Value "${cleanValue}" is below minimum length of ${limits.minChars} characters`,
        field: field.name,
        value: cleanValue,
        position: value.position,
      });
    }

    // Numeric range limits
    if (limits.range && fieldConfig.data_types.includes('number')) {
      const numValue = Number(cleanValue);
      if (!isNaN(numValue)) {
        if (numValue < limits.range.min) {
          errors.push({
            type: 'semantic',
            subtype: 'invalid_value',
            message: `Value ${numValue} is below minimum of ${limits.range.min}`,
            field: field.name,
            value: cleanValue,
            position: value.position,
          });
        }
        if (numValue > limits.range.max) {
          errors.push({
            type: 'semantic',
            subtype: 'invalid_value',
            message: `Value ${numValue} exceeds maximum of ${limits.range.max}`,
            field: field.name,
            value: cleanValue,
            position: value.position,
          });
        }
      }
    }
  }

  // Helper methods
  private getFieldConfig(fieldName: string): FilterCriteria | null {
    return this.filterOptions[fieldName.toUpperCase()] || null;
  }

  private isBooleanField(fieldName: string): boolean {
    const fieldConfig = this.getFieldConfig(fieldName);
    return fieldConfig?.data_types?.includes('boolean') || false;
  }

  private isWildcardValue(value: string): boolean {
    return value.includes('*');
  }

  private operatorToHumanOperator(operator: string): HumanOperator | null {
    switch (operator) {
      case '=':
        return HumanOperator.Equal;
      case '!=':
        return HumanOperator.NotEqual;
      case '>':
        return HumanOperator.GreaterThan;
      case '<':
        return HumanOperator.LessThan;
      case '>=':
        return HumanOperator.GreaterThanOrEqual;
      case '<=':
        return HumanOperator.LessThanOrEqual;
      case 'INSIDE_AREA':
        return HumanOperator.InsideArea;
      case 'OUTSIDE_AREA':
        return HumanOperator.OutsideArea;
      case 'INSIDE':
        return HumanOperator.InsideCircle;
      case 'OUTSIDE':
        return HumanOperator.OutsideCircle;
      default:
        return null;
    }
  }

  private humanOperatorToSymbol(humanOp: HumanOperator): string {
    switch (humanOp) {
      case HumanOperator.Equal:
        return '=';
      case HumanOperator.NotEqual:
        return '!=';
      case HumanOperator.GreaterThan:
        return '>';
      case HumanOperator.LessThan:
        return '<';
      case HumanOperator.GreaterThanOrEqual:
        return '>=';
      case HumanOperator.LessThanOrEqual:
        return '<=';
      case HumanOperator.Contains:
        return '= "*value*"';
      case HumanOperator.NotContains:
        return '!= "*value*"';
      case HumanOperator.StartsWith:
        return '= "value*"';
      case HumanOperator.EndsWith:
        return '= "*value"';
      default:
        return humanOp.toString();
    }
  }

  private getSimilarFields(input: string): string[] {
    const fieldNames = Object.keys(this.filterOptions);
    return this.getStringSuggestions(input, fieldNames, 3);
  }

  private getSimilarValues(input: string, values: any[]): string[] {
    const stringValues = values.map((v) => v.toString());
    return this.getStringSuggestions(input, stringValues, 5);
  }

  private getStringSuggestions(
    input: string,
    candidates: string[],
    maxSuggestions: number
  ): string[] {
    const inputLower = input.toLowerCase();

    // Score suggestions based on similarity
    const scored = candidates
      .map((candidate) => ({
        value: candidate,
        score: this.calculateStringSimilarity(
          inputLower,
          candidate.toLowerCase()
        ),
      }))
      .filter((item) => item.score > 0.3) // Only suggest reasonably similar items
      .sort((a, b) => b.score - a.score)
      .slice(0, maxSuggestions)
      .map((item) => item.value);

    return scored;
  }

  private calculateStringSimilarity(a: string, b: string): number {
    // Simple similarity score based on common characters and length
    if (a === b) return 1;
    if (a.length === 0 || b.length === 0) return 0;

    // Check if one string starts with the other
    if (a.startsWith(b) || b.startsWith(a)) return 0.8;

    // Count common characters
    const aChars = new Set(a);
    const bChars = new Set(b);
    const commonChars = new Set([...aChars].filter((char) => bChars.has(char)));

    return commonChars.size / Math.max(aChars.size, bChars.size);
  }
}
